// D-1121: アラートの閾値をコードに埋めず 1 か所(app_config の `alert_` で始まる行)から読む。
//   ★登録表(ALERT_THRESHOLDS)が正。画面に出す名前・既定値・範囲・段階の並びはここだけに書く。
//     設定画面(/admin/settings/alerts)も、アラートを出す画面も、この表と関数だけを見る。
//   ★取れないときはコードの既定値で動く(fail-safe。appConfig.js の fetchMinimumSupportedVersion と同じ null 安全の形)。
//     エラー・行が無い・数字でない・範囲外・段階の並びが崩れている → その値(並びはそのグループ)を既定値にする。
//   ★段階のある警告は「重い順に判定して最初に当たったもの」(tierFor)。並びが崩れた設定は保存させない(validateThresholdInput)。
//   ★編集できるのは admin だけ(app_config の書き込み policy は D-457 のまま admin だけ。画面も AdminRoute)。
//
// D-1122: 既存のアラート 10 件(巡回もれ・報告の滞留・集計が古い・請書の滞留・入荷待ち・景品の投入からの日数・
//   月次目標の達成率・利益率カレンダー・時間帯の人数不足・長時間勤務)をこの表に載せ替えた。
//   ★既定値 = 載せ替える前のコードの値。比べ方(compare)も前のコードのまま。変わったのは「数字の置き場」だけ。
//   ★比べ方: below = 値より小さいと当たる / at_or_above = 値以上 / above = 値を超える / at_or_below = 値以下。
//   ★グループは 2 種類: ordered(段階。重い順に並べ、並びを検査する)と、そうでないもの(別々の値。並びを見ない)。
//     段階の並び: below / at_or_below は重い段ほど値が小さい(赤 < 橙 < 黄)、at_or_above / above は重い段ほど大きい(赤 > 橙 > 黄)。
//   ★integer = 整数だけ(日・人)。時間・%・倍は小数も入れられる。
import { useCallback, useEffect, useState } from 'react'
import { supabase } from './supabase'

export const ALERT_KEY_PREFIX = 'alert_'

/** 設定画面の場所の並び(spec D-1122 DESIGN 5)。 */
export const ALERT_SECTIONS = [
  { key: 'launcher', label: 'ランチャー' },
  { key: 'ops', label: '運用' },
  { key: 'tanasupport', label: 'タナサポ' },
  { key: 'clawsupport', label: 'クレサポ' },
  { key: 'analysis', label: '分析' },
  { key: 'store', label: '店舗' },
]

/**
 * アラートのグループ。
 *   ordered: true の tiers は重い順(赤 → 橙 → 黄)。並びは compare で決まる(上の ★)。
 *   ordered: false の tiers は別々の値(並びを見ない。画面には書いた順に出す)。
 */
export const ALERT_GROUPS = [
  // ★D-1121 のグループを先頭のまま置く(ALERT_GROUPS[0] を見ているテストがある)。画面の並びは section で決める。
  {
    key: 'prize_assist_ratio', section: 'analysis', ordered: true, compare: 'below',
    label: '景品ランキング: 1個あたりプレイ数 ÷ アシスト',
    description: 'ブース明細の「1個あたり」(プレイ数 ÷ 出数)を設定の A(アシスト回数)で割った比が、'
      + 'この値より小さいとアラートを出します。重い順に 赤 < 橙 < 黄 にしてください。',
    tiers: [
      { level: 'red',    key: 'alert_prize_assist_ratio_red' },
      { level: 'orange', key: 'alert_prize_assist_ratio_orange' },
      { level: 'yellow', key: 'alert_prize_assist_ratio_yellow' },
    ],
  },
  {
    key: 'patrol_overdue', section: 'launcher', ordered: false, compare: 'at_or_above',
    label: '巡回もれ',
    description: '最終巡回からこの日数以上空いた店を、ランチャーの「巡回もれ」に出します。',
    tiers: [{ level: 'on', key: 'alert_patrol_overdue_days' }],
  },
  {
    key: 'report_stale', section: 'launcher', ordered: false, compare: 'above',
    label: '報告の滞留',
    description: 'ランチャーの報告で、出してからこの時間を超えて残っている行に赤い印を付けます。',
    tiers: [
      { level: 'stopped', key: 'alert_report_stale_stopped_hours' },
      { level: 'fault', key: 'alert_report_stale_fault_hours' },
    ],
  },
  {
    key: 'ops_stats_stale', section: 'ops', ordered: false, compare: 'at_or_above',
    label: '運用の見張り: 集計が古い',
    description: '夜間集計の最終日がこの日数以上前なら「古い」として知らせます。',
    tiers: [{ level: 'on', key: 'alert_ops_stats_stale_days' }],
  },
  {
    key: 'order_ack_pending', section: 'ops', ordered: false, compare: 'above',
    label: '請書の取込監視: 読み取り待ち',
    description: '取り込んだ請書の中身が読まれないまま、この日数を超えたら「止まってる」に出します。',
    tiers: [{ level: 'on', key: 'alert_order_ack_pending_days' }],
  },
  {
    key: 'arrival_pending', section: 'tanasupport', ordered: true, compare: 'at_or_above',
    label: '未検収滞留: 経過日数の色',
    description: '経過日数がこの日数以上になると色を付けます。重い順に 赤 > 橙 > 黄 にしてください。',
    tiers: [
      { level: 'red', key: 'alert_arrival_pending_red_days' },
      { level: 'orange', key: 'alert_arrival_pending_orange_days' },
      { level: 'yellow', key: 'alert_arrival_pending_yellow_days' },
    ],
  },
  {
    key: 'prize_age', section: 'clawsupport', ordered: true, compare: 'at_or_above',
    label: '巡回の履歴: 景品の投入からの日数',
    description: '景品名の右の日数(D40 など)がこの日数以上になると強調します。枠 > 太字 にしてください。',
    tiers: [
      { level: 'frame', key: 'alert_prize_age_frame_days' },
      { level: 'bold', key: 'alert_prize_age_bold_days' },
    ],
  },
  {
    key: 'monthly_target', section: 'analysis', ordered: true, compare: 'at_or_above',
    label: '月次目標: 達成率の色',
    description: '達成率がこの値以上で緑・黄、それより下は赤にします(縦と表の両方)。緑 > 黄 にしてください。',
    tiers: [
      { level: 'green', key: 'alert_monthly_target_green_pct' },
      { level: 'yellow', key: 'alert_monthly_target_yellow_pct' },
    ],
  },
  {
    key: 'profit_calendar', section: 'analysis', ordered: false, compare: 'at_or_above',
    label: '利益率カレンダー: 目標と色',
    description: '粗利率が目標以上で緑、目標から黄の幅までの下振れで黄、それより下は赤にします。目標は画面でも変えられます(ここは最初に出る値)。',
    tiers: [
      { level: 'target', key: 'alert_profit_calendar_target_pct' },
      { level: 'band', key: 'alert_profit_calendar_yellow_band_pt' },
    ],
  },
  {
    key: 'shift_understaffed', section: 'store', ordered: false, compare: 'at_or_below',
    label: 'シフト: 時間帯の人数不足',
    description: '日のシフトの「時間帯ごとの人数」が、この人数以下の時間を赤にします。',
    tiers: [{ level: 'on', key: 'alert_shift_understaffed_people' }],
  },
  {
    key: 'shift_long_work', section: 'store', ordered: false, compare: 'at_or_above',
    label: '人件費: 長時間勤務の注意',
    description: '月の出勤がこの日数以上で、1 日の時間(通常 + 深夜)がこの時間以上の日がある人に注意を出します。',
    tiers: [
      { level: 'days', key: 'alert_shift_long_month_days' },
      { level: 'hours', key: 'alert_shift_long_day_hours' },
    ],
  },
]

const def = (key, group, level, label, unit, defaultValue, compare, integer, max, description) =>
  ({ key, group, level, label, unit, defaultValue, compare, integer, min: 0, max, description })

/** 登録表。key = app_config.config_key。defaultValue = 載せ替える前のコードの値。 */
export const ALERT_THRESHOLDS = [
  // D-1121
  {
    key: 'alert_prize_assist_ratio_yellow', group: 'prize_assist_ratio', level: 'yellow',
    label: '黄', unit: '倍', defaultValue: 1.0, compare: 'below', integer: false, min: 0, max: 100,
    description: 'この比より小さいと黄(アシスト前に出ている)',
  },
  {
    key: 'alert_prize_assist_ratio_orange', group: 'prize_assist_ratio', level: 'orange',
    label: '橙', unit: '倍', defaultValue: 0.7, compare: 'below', integer: false, min: 0, max: 100,
    description: 'この比より小さいと橙',
  },
  {
    key: 'alert_prize_assist_ratio_red', group: 'prize_assist_ratio', level: 'red',
    label: '赤', unit: '倍', defaultValue: 0.4, compare: 'below', integer: false, min: 0, max: 100,
    description: 'この比より小さいと赤',
  },
  // D-1122 #1 src/services/patrolOverdue.js PATROL_OVERDUE_THRESHOLD_DAYS(RPC fn_store_patrol_gaps は >=)
  def('alert_patrol_overdue_days', 'patrol_overdue', 'on', '日数', '日', 5, 'at_or_above', true, 365,
    'この日数以上で巡回もれ'),
  // #2 Launcher.jsx renderAlertRow(a, 24) / renderGroupedAlerts(fault, 24 * 7)(経過 > 時間)
  def('alert_report_stale_stopped_hours', 'report_stale', 'stopped', '停止中', '時間', 24, 'above', false, 8760,
    'この時間を超えた停止中の報告に印'),
  def('alert_report_stale_fault_hours', 'report_stale', 'fault', '不具合', '時間', 168, 'above', false, 8760,
    'この時間を超えた不具合の報告に印。168 時間 = 7 日'),
  // #3 src/services/opsWatch.js STATS_STALE_THRESHOLD_DAYS(>=)
  def('alert_ops_stats_stale_days', 'ops_stats_stale', 'on', '日数', '日', 2, 'at_or_above', true, 365,
    'この日数以上古いと知らせる'),
  // #4 src/services/orderAckAudit.js PENDING_WARN_DAYS(>)
  def('alert_order_ack_pending_days', 'order_ack_pending', 'on', '日数', '日', 3, 'above', true, 365,
    'この日数を超えたら止まってる'),
  // #5 src/tanasupport/pages/ArrivalPendingPage.jsx daysClass(>=)
  def('alert_arrival_pending_red_days', 'arrival_pending', 'red', '赤', '日', 30, 'at_or_above', true, 365,
    'この日数以上で赤'),
  def('alert_arrival_pending_orange_days', 'arrival_pending', 'orange', '橙', '日', 14, 'at_or_above', true, 365,
    'この日数以上で橙'),
  def('alert_arrival_pending_yellow_days', 'arrival_pending', 'yellow', '黄', '日', 7, 'at_or_above', true, 365,
    'この日数以上で黄'),
  // #6 src/clawsupport/components/BoothHistoryRow.jsx AGE_ALERT_DAYS / AGE_WARN_DAYS(>=)
  def('alert_prize_age_frame_days', 'prize_age', 'frame', '枠', '日', 90, 'at_or_above', true, 3650,
    'この日数以上で太字 + 枠'),
  def('alert_prize_age_bold_days', 'prize_age', 'bold', '太字', '日', 60, 'at_or_above', true, 3650,
    'この日数以上で太字'),
  // #14 MonthlyTargetPage.jsx toneOf / MonthlyTargetMatrix.jsx tone(rate)(>=)
  def('alert_monthly_target_green_pct', 'monthly_target', 'green', '緑', '%', 100, 'at_or_above', false, 1000,
    'この達成率以上で緑'),
  def('alert_monthly_target_yellow_pct', 'monthly_target', 'yellow', '黄', '%', 85, 'at_or_above', false, 1000,
    'この達成率以上で黄(下は赤)'),
  // #15 ProfitCalendarPage.jsx useState(70) / colorFor の target - 20(>=)
  def('alert_profit_calendar_target_pct', 'profit_calendar', 'target', '目標', '%', 70, 'at_or_above', false, 100,
    '最初に出る目標の粗利率'),
  def('alert_profit_calendar_yellow_band_pt', 'profit_calendar', 'band', '黄の幅', 'pt', 20, 'at_or_above', false, 100,
    '目標からこの幅までの下振れは黄'),
  // #16 src/storereport/StoreShiftsDaySheet.jsx count <= 1
  def('alert_shift_understaffed_people', 'shift_understaffed', 'on', '人数', '人', 1, 'at_or_below', true, 100,
    'この人数以下の時間を赤'),
  // #17 src/services/shiftPay.js LONG_MONTH_DAYS / LONG_DAY_HOURS(>=)
  def('alert_shift_long_month_days', 'shift_long_work', 'days', '出勤日数', '日', 16, 'at_or_above', true, 31,
    '月の出勤がこの日数以上'),
  def('alert_shift_long_day_hours', 'shift_long_work', 'hours', '1日の時間', '時間', 6, 'at_or_above', false, 24,
    'この時間以上の日があれば注意'),
]

const DEF_BY_KEY = Object.fromEntries(ALERT_THRESHOLDS.map(d => [d.key, d]))
const NUM_RE = /^\d+(\.\d+)?$|^\.\d+$/

/** key → 登録表の 1 行(無ければ null)。 */
export function thresholdDef(key) {
  return DEF_BY_KEY[key] ?? null
}

/** 既定値だけの { key: number }。 */
export function defaultThresholds() {
  return Object.fromEntries(ALERT_THRESHOLDS.map(d => [d.key, d.defaultValue]))
}

/**
 * 1 つの値を数値にする。数字でない・min 以下(0 以下)・max より大きい・整数だけの値に小数 → null。
 *   ★min は「より大きい」(0 は不可)。0 以下を許すと段階が消える。
 */
export function parseThresholdValue(d, raw) {
  if (!d) return null
  if (raw == null) return null
  const s = String(raw).trim()
  if (s === '' || !NUM_RE.test(s)) return null
  const n = Number(s)
  if (!Number.isFinite(n)) return null
  if (n <= d.min || n > d.max) return null
  if (d.integer && !Number.isInteger(n)) return null
  return n
}

/**
 * グループの値の並びが守られているか。
 *   ordered のグループ: below / at_or_below は重い順に値が小さい(赤 < 橙 < 黄)、at_or_above / above は大きい(赤 > 橙 > 黄)。
 *   ordered でないグループは数であることだけを見る。
 */
export function isGroupOrderValid(group, values) {
  const vs = group.tiers.map(t => values[t.key])
  if (vs.some(v => typeof v !== 'number' || !Number.isFinite(v) || v <= 0)) return false
  if (!group.ordered) return true
  const heavierIsSmaller = group.compare === 'below' || group.compare === 'at_or_below'
  for (let i = 1; i < vs.length; i += 1) {
    if (heavierIsSmaller ? !(vs[i - 1] < vs[i]) : !(vs[i - 1] > vs[i])) return false
  }
  return true
}

/**
 * app_config の行 → { key: number }。
 *   ★取れない値は既定値。並びが崩れたグループは丸ごと既定値(片方だけ既定にすると並びがまた崩れうる)。
 */
export function resolveThresholds(rows) {
  const out = defaultThresholds()
  for (const r of Array.isArray(rows) ? rows : []) {
    const d = DEF_BY_KEY[r?.config_key]
    if (!d) continue
    const n = parseThresholdValue(d, r.config_value)
    if (n != null) out[d.key] = n
  }
  for (const g of ALERT_GROUPS) {
    if (!isGroupOrderValid(g, out)) {
      for (const t of g.tiers) out[t.key] = DEF_BY_KEY[t.key].defaultValue
    }
  }
  return out
}

function fieldError(d, raw) {
  const s = raw == null ? '' : String(raw).trim()
  if (s === '') return `${d.label}: 値が空です`
  if (!NUM_RE.test(s)) return `${d.label}: 数字で入れてください`
  const n = Number(s)
  if (n <= d.min) return `${d.label}: 0 より大きい値にしてください`
  if (n > d.max) return `${d.label}: ${d.max} 以下にしてください`
  return `${d.label}: 整数で入れてください`
}

/**
 * 設定画面の入力(文字列)を検査する。
 *   ★入力に入っている key のグループだけを見る(D-1122: グループ単位で保存する)。グループの中で欠けている値は「空」。
 *   @returns {{ ok: boolean, values: Record<string,number>, fieldErrors: Record<string,string>, groupErrors: Record<string,string> }}
 */
export function validateThresholdInput(input) {
  const values = {}
  const fieldErrors = {}
  const groupErrors = {}
  const has = k => input != null && Object.prototype.hasOwnProperty.call(input, k)
  const groups = ALERT_GROUPS.filter(g => g.tiers.some(t => has(t.key)))
  for (const g of groups) {
    for (const t of g.tiers) {
      const d = DEF_BY_KEY[t.key]
      const raw = input[t.key]
      const n = parseThresholdValue(d, raw)
      if (n == null) fieldErrors[d.key] = fieldError(d, raw)
      else values[d.key] = n
    }
    if (g.tiers.some(t => fieldErrors[t.key])) continue
    if (!isGroupOrderValid(g, values)) {
      const heavierIsSmaller = g.compare === 'below' || g.compare === 'at_or_below'
      const tiersBigFirst = heavierIsSmaller ? [...g.tiers].reverse() : g.tiers
      const labels = tiersBigFirst.map(t => DEF_BY_KEY[t.key].label)
      groupErrors[g.key] = `並びが崩れています: ${labels.join(' > ')} > 0 の順に大きくしてください`
    }
  }
  const ok = Object.keys(fieldErrors).length === 0 && Object.keys(groupErrors).length === 0
  // ★values は登録表の順に並べ直す(保存する行の順 = 登録表の順)。
  const ordered = Object.fromEntries(ALERT_THRESHOLDS.filter(d => d.key in values).map(d => [d.key, values[d.key]]))
  return { ok, values: ordered, fieldErrors, groupErrors }
}

/** 1 つの値と閾値を比べ方どおりに比べる。 */
export function compareHit(compare, value, threshold) {
  if (typeof value !== 'number' || !Number.isFinite(value)) return false
  if (typeof threshold !== 'number' || !Number.isFinite(threshold)) return false
  switch (compare) {
    case 'below': return value < threshold
    case 'at_or_above': return value >= threshold
    case 'above': return value > threshold
    case 'at_or_below': return value <= threshold
    default: return false
  }
}

/**
 * 段階の判定。重い順に見て最初に当たった段を返す。当たらなければ null。
 *   ★value が数でない・有限でないときは null(アラートを出さない)。
 *   ★比べ方は値ごとの compare(グループの compare と同じ)。
 */
export function tierFor(group, thresholds, value) {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null
  for (const t of group.tiers) {
    const compare = DEF_BY_KEY[t.key]?.compare ?? group.compare
    if (compareHit(compare, value, thresholds?.[t.key])) return t.level
  }
  return null
}

export function groupByKey(key) {
  return ALERT_GROUPS.find(g => g.key === key) ?? null
}

/**
 * app_config から `alert_` の行をまとめて取る。★投げない。失敗しても既定値を返す。
 */
export async function fetchAlertThresholds() {
  try {
    const { data, error } = await supabase
      .from('app_config')
      .select('config_key, config_value')
      .like('config_key', `${ALERT_KEY_PREFIX}%`)
      .order('config_key')
      .limit(200)
    if (error) return defaultThresholds()
    return resolveThresholds(data)
  } catch {
    return defaultThresholds()
  }
}

// D-1122: 同時に開いた画面・行(巡回の履歴は 1 画面に 15 行)が 1 回の読み込みを分け合う。
//   ★結果は持ち続けない(読み終わったら次は読み直す)。保存した値を次に開いたときに拾うため。
let inflight = null
export function fetchAlertThresholdsShared() {
  if (!inflight) inflight = fetchAlertThresholds().finally(() => { inflight = null })
  return inflight
}

/**
 * 保存。★検査を通らなければ書かない(例外で理由を返す)。入力にあるグループの行を 1 回の upsert で入れる(1 文 = 全部か何もなし)。
 *   updated_by = 今のスタッフ。updated_at はこちらで入れる(表に更新トリガーが無い)。
 */
export async function saveAlertThresholds(input, staffId) {
  const v = validateThresholdInput(input)
  if (!v.ok) {
    const err = new Error('保存できない値があります')
    err.validation = v
    throw err
  }
  const keys = Object.keys(v.values)
  if (keys.length === 0) return v.values
  const now = new Date().toISOString()
  const rows = keys.map(k => ({
    config_key: k,
    config_value: String(v.values[k]),
    updated_by: staffId ?? null,
    updated_at: now,
  }))
  const { error } = await supabase.from('app_config').upsert(rows, { onConflict: 'config_key' })
  if (error) throw new Error(error.message || '保存に失敗しました')
  return v.values
}

/** 画面用。最初は既定値で描き、取れたら差し替える(取れなくても既定値のまま動く)。 */
export function useAlertThresholds() {
  const [thresholds, setThresholds] = useState(defaultThresholds)
  const [loading, setLoading] = useState(true)
  const reload = useCallback(async () => {
    setLoading(true)
    const t = await fetchAlertThresholds()
    setThresholds(t)
    setLoading(false)
    return t
  }, [])
  useEffect(() => {
    let alive = true
    fetchAlertThresholdsShared().then(t => {
      if (!alive) return
      setThresholds(t)
      setLoading(false)
    })
    return () => { alive = false }
  }, [])
  return { thresholds, loading, reload }
}
